/**
 * UnsavedChangesDialog — the three-way question of every form with unsaved changes (#3793, #3903):
 * keep editing, leave without saving, or save and leave. It names the fields that
 * changed, never their values. Built on the shared Modal (focus trap, Escape,
 * `aria-modal`); `ConfirmDialog` has two buttons and cannot ask this.
 */

import { useEffect, useRef } from 'react'

import { useTranslation } from '@platform/i18n/hooks/useTranslation'

import Modal from '@/shared/components/ui/Modal/Modal'

import type { SaveFailure } from './UnsavedChangesProvider'

interface UnsavedChangesDialogProps {
  isOpen: boolean
  /** The changed fields as a sentence («el remitente y «Responder a»»). */
  fieldsLabel: string
  fieldCount: number
  saving: boolean
  /** Another save of a form is running: «Save and leave» is disabled and the dialog says why. */
  saveBlocked?: boolean
  /** Saving threw or timed out: the dialog stays open and says so. */
  saveFailure: SaveFailure | null
  /** With `saveFailure`: the form whose save failed (several forms may be leaving). */
  failedFields?: string | null
  /** With `saveFailure`: the failed form is gone, so «Save and leave» is not offered. */
  saveUnavailable?: boolean
  onStay: () => void
  onDiscard: () => void
  onSaveAndLeave: () => void
}

const noop = () => undefined

// The same classes as the admin buttons: `shared/` does not import from `modules/`.
const PRIMARY_BUTTON =
  'inline-flex items-center justify-center rounded-md bg-crypto-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-crypto-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-crypto-primary disabled:cursor-not-allowed disabled:opacity-60'
const SECONDARY_BUTTON =
  'inline-flex items-center justify-center rounded-md border border-crypto-border bg-crypto-darker px-4 py-2 text-sm font-medium text-crypto-text hover:bg-crypto-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-crypto-primary disabled:cursor-not-allowed disabled:opacity-60'

export function UnsavedChangesDialog({
  isOpen,
  fieldsLabel,
  fieldCount,
  saving,
  saveBlocked = false,
  saveFailure,
  failedFields = null,
  saveUnavailable = false,
  onStay,
  onDiscard,
  onSaveAndLeave,
}: UnsavedChangesDialogProps) {
  const { t } = useTranslation('common')
  const stayRef = useRef<HTMLButtonElement>(null)
  const bodyRef = useRef<HTMLParagraphElement>(null)

  // The focus trap lands on the first focusable element (the ✕); the safe option is «keep editing».
  // While saving the three buttons are disabled (and the ✕ is inert): the focus stays inside the
  // dialog, on its text.
  useEffect(() => {
    if (isOpen) (saving ? bodyRef : stayRef).current?.focus()
  }, [isOpen, saving])

  return (
    <Modal
      isOpen={isOpen}
      // Nothing closes the dialog halfway through a save.
      onClose={saving ? noop : onStay}
      closeOnBackdrop={!saving}
      title={t('unsavedChanges.title', 'You have unsaved changes')}
      size="md"
      closeButtonTestId="unsaved-close"
    >
      {saving ? (
        <p ref={bodyRef} tabIndex={0} className="text-sm text-crypto-text focus:outline-none">
          {t('unsavedChanges.savingBody', 'Saving… you will leave as soon as it finishes.')}
        </p>
      ) : (
        <>
          <p className="text-sm text-crypto-text">
            {t('unsavedChanges.body', {
              count: fieldCount,
              fields: fieldsLabel,
              defaultValue: `You changed ${fieldsLabel} and have not saved it.`,
            })}
          </p>
          <p className="mt-1 text-sm text-crypto-muted">
            {t('unsavedChanges.hint', 'If you leave, they are lost.')}
          </p>
          {saveFailure && (
            <div
              role="alert"
              data-testid="unsaved-save-error"
              className="mt-3 text-sm text-red-400 [html[data-theme=light]_&]:text-red-700"
            >
              {failedFields && (
                <p data-testid="unsaved-failed-form">
                  {t('unsavedChanges.failedForm', {
                    fields: failedFields,
                    defaultValue: `Not saved: ${failedFields}.`,
                  })}
                </p>
              )}
              <p>
                {saveFailure === 'timeout'
                  ? t(
                      'unsavedChanges.saveTimedOut',
                      'Saving took too long and could not be confirmed; it may still finish. You are still on this page.',
                    )
                  : t(
                      'unsavedChanges.saveFailed',
                      'Your changes could not be saved. You are still on this page.',
                    )}
              </p>
            </div>
          )}
          {saveBlocked && (
            <p data-testid="unsaved-save-in-progress" className="mt-3 text-sm text-crypto-muted">
              {t(
                'unsavedChanges.saveInProgress',
                'A save is in progress. Wait for it to finish or discard your changes.',
              )}
            </p>
          )}
        </>
      )}
      <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
        <button
          ref={stayRef}
          type="button"
          className={SECONDARY_BUTTON}
          disabled={saving}
          data-testid="unsaved-stay"
          onClick={onStay}
        >
          {t('unsavedChanges.stay', 'Keep editing')}
        </button>
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <button
            type="button"
            className={SECONDARY_BUTTON}
            disabled={saving}
            data-testid="unsaved-discard"
            onClick={onDiscard}
          >
            {t('unsavedChanges.discard', 'Leave without saving')}
          </button>
          {!(saveFailure && saveUnavailable) && (
            <button
              type="button"
              className={PRIMARY_BUTTON}
              disabled={saving || saveBlocked}
              data-testid="unsaved-save-and-leave"
              onClick={onSaveAndLeave}
            >
              {saving
                ? t('unsavedChanges.saving', 'Saving…')
                : t('unsavedChanges.saveAndLeave', 'Save and leave')}
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}
